import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { GroupsData, NewGroupService } from 'data/service/group-service';
import { NewUserStore } from './new-user.store';

type GroupState = {
  productGroups: {
    productId: string;
    groups: GroupsData;
    selectedGroupId?: string;
  }[];
  isLoading: boolean;
  selectedProductId?: string;
  standalone: boolean;
};

const initialState: GroupState = {
  productGroups: [],
  isLoading: false,
  selectedProductId: undefined,
  standalone: false,
};

export const NewGroupStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({
    allGroups: computed(() => {
      if (store.standalone()) return null;

      const productId = store.selectedProductId();
      const productGroup = store
        .productGroups()
        .find((pg) => pg.productId === productId);

      return productGroup?.groups ?? [];
    }),
    selectedGroup: computed(() => {
      if (store.standalone()) return null;

      const productId = store.selectedProductId();
      const productGroup = store
        .productGroups()
        .find((pg) => pg.productId === productId);

      if (!productGroup) return null;

      const group = productGroup.groups.find(
        (g) => g.id === productGroup.selectedGroupId
      ) ?? null;

      return group;
    }),
  })),

  withMethods((store, 
    service = inject(NewGroupService),
    userStore = inject(NewUserStore)
  ) => ({
    async loadUserGroups(productId: string): Promise<boolean> {
      if (store.isLoading()) return false;

      patchState(store, { isLoading: true });

      try {
        const groupData = await service.findGroupsByProduct(productId);
        const firstGroupId = groupData[0]?.id;

        patchState(store, {
          isLoading: false,
          selectedProductId: productId,
          productGroups: [
            {
              productId,
              groups: groupData,
              selectedGroupId: firstGroupId ?? undefined,
            },
          ],
        });

        return true;
      } catch (error) {
        console.error('Failed to load user groups:', error);
        patchState(store, {
          isLoading: false,
          selectedProductId: undefined,
        });
        return false;
      }
    },
    getGroupById(groupId: string) {
      const productId = store.selectedProductId();
      if (!productId) return undefined;

      const productGroup = store
        .productGroups()
        .find(pg => pg.productId === productId);

      return productGroup?.groups.find(g => g.id === groupId);
    },
    getGroupByIdSignal(groupId: string) {
      return computed(() => {
        const productId = store.selectedProductId();
        if (!productId) return undefined;

        const productGroup = store
          .productGroups()
          .find(pg => pg.productId === productId);

        return productGroup?.groups.find(g => g.id === groupId);
      });
    },
    selectGroup(groupId: string): void {
      const productId = store.selectedProductId();
      const productGroup = store
        .productGroups()
        .find((pg) => pg.productId === productId);

      if (!productGroup) return;

      patchState(store, {
        productGroups: store.productGroups().map((pg) =>
          pg.productId === productId
            ? { ...pg, selectedGroupId: groupId }
            : pg
        ),
      });
    },
    setStandalone(standalone: boolean) {
      patchState(store, {
        standalone: standalone,
      });
    },
    async removeMember(groupId: string, memberId: string) {
      try {
        await service.removeMemberOrLeaveGroup(groupId, memberId);

        patchState(store, {
          productGroups: store.productGroups().map(pg => {
            if (pg.productId !== store.selectedProductId()) return pg;

            const updatedGroups = pg.groups.map(group => {
              if (group.id !== groupId) return group;
              return {
                ...group,
                members: group.members.filter(m => m.id !== memberId),
              };
            });

            return { ...pg, groups: updatedGroups };
          }),
        });
      } catch (error) {
        console.error('Failed to remove member:', error);
      }
    },

    async leaveGroup(groupId: string, currentUserId: string) {
      try {
        await service.removeMemberOrLeaveGroup(groupId, currentUserId);

        patchState(store, {
          productGroups: store.productGroups().map(pg => {
            if (pg.productId !== store.selectedProductId()) return pg;

            const updatedGroups = pg.groups.filter(g => g.id !== groupId);
            const isSelected = pg.selectedGroupId === groupId;
            const newSelectedId = isSelected ? updatedGroups[0]?.id : pg.selectedGroupId;

            return {
              ...pg,
              groups: updatedGroups,
              selectedGroupId: newSelectedId,
            };
          }),
        });
      } catch (error) {
        console.error('Failed to leave group:', error);
      }
    },
    async closeGroup(groupId: string) {
      try {
        await service.closeGroup(groupId);

        patchState(store, {
          productGroups: store.productGroups().map(pg => {
            if (pg.productId !== store.selectedProductId()) return pg;

            const updatedGroups = pg.groups.filter(g => g.id !== groupId);
            const isSelected = pg.selectedGroupId === groupId;
            const newSelectedId = isSelected ? updatedGroups[0]?.id : pg.selectedGroupId;

            return {
              ...pg,
              groups: updatedGroups,
              selectedGroupId: newSelectedId,
            };
          }),
        });
      } catch (error) {
        console.error('Failed to close group:', error);
      }

    },
    async createGroup(name: string): Promise<boolean> {
      if (!store.selectedProductId()) return;

      try {
        const newGroup = await service.createGroup(name, store.selectedProductId());
        const currentUser = userStore.currentUser();
        
        const newGroupData = {
          id: newGroup.id,
          name: newGroup.name,
          owner:true,
          members: [{
            id: currentUser.id, // Replace with actual current user ID
            owner: true
          }],
        }

        patchState(store, {
          productGroups: store.productGroups().map(pg => {
            if (pg.productId !== store.selectedProductId()) {
              return pg;
            } else {

              const updatedGroups = [...pg.groups, newGroupData];
              return {
                ...pg,
                groups: updatedGroups,
                selectedGroupId: newGroup.id, // optional: auto-select the new group
              };
            }
          }),
        });
        return true;

      } catch (error) {
        console.error('Failed to create group:', error);
        return false;
      }
    },

  }))
);
